import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { X, Package, Loader2, AlertCircle, Search, Users, UserCheck, Check, Image, JapaneseYen, CheckCircle2, Type, ChevronDown, ChevronUp } from 'lucide-react';
import { BannerAssetDefaults, Product, ProductImage } from '../../types/knowledge';
import knowledgeApiService from '../../services/api/knowledge/knowledgeApiService';
import imageGenerationService, { PromptOptions } from '../../services/api/imageGenerationService';
import SegmentAppealPicker, { SegmentAppealSelection } from './SegmentAppealPicker';
import { TaskySelect } from '../tasky';

// 旧 ProductSelectionModal.scss を内製化（Wave 3d / T20）。
// 本 SCSS は `.modal-*` / `.form-*` / `.cancel-button` 等をスコープなしで定義しており、
// 逆に `.cancel-button` / `.confirm-button` は先行する ProductImageSelectionModal.scss の
// `.modal-footer .cancel-button`（詳細度 0,2,0）に負けていた。ここでは
// 「マージ後の実効値」= フッターのボタンは PISM 側の寸法・確定ボタンは青(#3b82f6) を内製化している。
// `.form-select` の矢印（data URI の背景画像）は TaskySelect 側の ChevronDown に置き換えた。

export interface ProductSelectionData {
  product: Product;
  selectedImages: ProductImage[];
  segmentId: string;
  segmentName: string;
  appealId: string;
  appealName: string;
  appealDirection: string;
  additionalContext: string;
  includePerson: string;
  textlessMode?: boolean;
}

interface ProductSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: ProductSelectionData) => void;
  /**
   * 使う画面。既定はバナー（参照画像・人物表示・テキストなしの欄を出す）。
   * 'video'（スタイル動画）は商品と「誰に、何を伝えるか」だけを選ぶ。画像は動画側が自動で選ぶので出さず、
   * 訴求を選んでもらうためにアコーディオンを開いた状態で始める。
   */
  purpose?: 'banner' | 'video';
}

const ProductSelectionModal: React.FC<ProductSelectionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  purpose = 'banner',
}) => {
  const isBanner = purpose === 'banner';
  // Product selection state
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [productLoading, setProductLoading] = useState(false);
  const [productError, setProductError] = useState<string | null>(null);
  const [productSearchQuery, setProductSearchQuery] = useState('');

  // Prompt options state
  const [promptOptions, setPromptOptions] = useState<PromptOptions | null>(null);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [includePerson, setIncludePerson] = useState<string>('');

  // Image selection state
  const [selectedImages, setSelectedImages] = useState<ProductImage[]>([]);
  // 初期選択は MCP と同じ素材の役割判定（サーバー）で決める。null は未取得
  const [assetDefaults, setAssetDefaults] = useState<BannerAssetDefaults | null>(null);
  // 利用者が画像を選び直したら、あとから届いた判定で上書きしない
  const userPickedImagesRef = useRef(false);
  // 判定の取得中は確定させない（空の選択で確定すると、前の商品の参照画像が残る）
  const [assetDefaultsLoading, setAssetDefaultsLoading] = useState(false);

  // Segment and appeal selection state
  // 戦略マップのマス目から「ペルソナ×訴求」を1クリックで選ぶため、
  // 選択結果（ID と表示名）をひとまとまりで保持する。
  const [selectedIntent, setSelectedIntent] = useState<SegmentAppealSelection | null>(null);
  // 戦略マップは使う人が限られ縦に大きいので、商品情報の「基本情報」と同じく
  // アコーディオンに畳み、モーダルを開くたびに閉じた状態から始める。
  const [isIntentOpen, setIsIntentOpen] = useState(!isBanner);

  // Textless mode
  const [textlessMode, setTextlessMode] = useState(false);

  // ターゲット名／訴求の狙いは、戦略マップで選んだものを優先し、未選択なら
  // 商品に登録された既定値へフォールバックする（旧UIと同じ挙動）。
  const targetSegmentName = useMemo(() => {
    if (selectedIntent?.segmentName) return selectedIntent.segmentName;
    return selectedProduct?.extended_metadata?.target?.segment || '';
  }, [selectedIntent, selectedProduct]);

  const appealDirection = useMemo(() => {
    if (selectedIntent?.appealDirection) return selectedIntent.appealDirection;
    if (selectedIntent?.appealName) return selectedIntent.appealName;
    return selectedProduct?.extended_metadata?.appeal?.direction || '';
  }, [selectedIntent, selectedProduct]);

  // Get available images from product
  // 主役・自社ロゴと判定された画像は、参照画像として渡す category も判定に合わせる
  // （UI スクショが「素材」として登録されていても、主役の実写として渡す）
  const availableImages = useMemo<ProductImage[]>(() => {
    const images = selectedProduct?.product_images || [];
    if (!assetDefaults || assetDefaults.product_id !== selectedProduct?.id) return images;
    return images.map((image) => {
      const curated = assetDefaults.default_images.find((entry) => entry.image_id === image.id);
      return curated ? { ...image, category: curated.category } : image;
    });
  }, [selectedProduct, assetDefaults]);

  // Logo images for the "ロゴのみ選択" shortcut
  // 判定が取れていれば自社ロゴだけ（brand_logo に入っている導入企業ロゴや機能アイコンは選ばない）
  const logoImages = useMemo<ProductImage[]>(() => {
    if (assetDefaults && assetDefaults.product_id === selectedProduct?.id) {
      return availableImages.filter(img => img.id === assetDefaults.logo_image_id);
    }
    return availableImages.filter(img => img.category === 'brand_logo');
  }, [availableImages, assetDefaults, selectedProduct?.id]);

  // Get product price display
  const priceDisplay = useMemo(() => {
    if (!selectedProduct) return null;
    const pricing = selectedProduct.extended_metadata?.pricing_offer;
    return {
      price: pricing?.price_details || '',
      offer: pricing?.offers_campaigns || '',
      cta: pricing?.main_cta || '',
    };
  }, [selectedProduct]);

  // Fetch products and prompt options when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchProducts();
      if (isBanner) fetchPromptOptions();
    } else {
      // Reset all state when modal closes
      setSelectedProduct(null);
      setProductSearchQuery('');
      setProductError(null);
      setIncludePerson('');
      setSelectedIntent(null);
      setIsIntentOpen(!isBanner);
      setSelectedImages([]);
      setAssetDefaults(null);
      setTextlessMode(false);
    }
  }, [isOpen]);

  // 商品を選んだら、MCP と同じ素材の役割判定で主役画像と自社ロゴを初期選択する。
  // 登録時の is_primary や並び順では、封筒アイコンのような装飾素材が「メイン」になっていた。
  // 判定が取れないときだけ従来の初期値（is_primary、無ければ先頭）に戻す。
  useEffect(() => {
    userPickedImagesRef.current = false;
    setAssetDefaults(null);
    setSelectedImages([]);
    setAssetDefaultsLoading(false);
    const product = selectedProduct;
    const images = product?.product_images || [];
    // 動画では参照画像を選ばない（判定を待たずに確定できるようにする）
    if (!isBanner || !product || images.length === 0) return undefined;
    let cancelled = false;
    setAssetDefaultsLoading(true);
    knowledgeApiService.getBannerAssetDefaults(product.id)
      .then((defaults) => {
        if (cancelled) return;
        setAssetDefaults(defaults);
        if (userPickedImagesRef.current) return;
        setSelectedImages(defaults.default_images
          .map((entry) => {
            const image = images.find((candidate) => candidate.id === entry.image_id);
            return image ? { ...image, category: entry.category } : null;
          })
          .filter((image): image is ProductImage => image !== null));
      })
      .catch(() => {
        if (cancelled || userPickedImagesRef.current) return;
        const primaryImage = images.find(img => img.is_primary);
        setSelectedImages(primaryImage ? [primaryImage] : [images[0]]);
      })
      .finally(() => {
        if (!cancelled) setAssetDefaultsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct?.id]);

  // Reset segment / appeal selection when product changes (default to "選択なし")
  useEffect(() => {
    setSelectedIntent(null);
  }, [selectedProduct?.id]);

  const fetchProducts = async () => {
    setProductLoading(true);
    setProductError(null);
    try {
      const data = await knowledgeApiService.getProducts({ status: 'active' });
      setProducts(data);
    } catch (err: any) {
      console.error('Failed to fetch products:', err);
      setProductError(err?.message || '商品の取得に失敗しました');
    } finally {
      setProductLoading(false);
    }
  };

  const fetchPromptOptions = async () => {
    setOptionsLoading(true);
    try {
      const response = await imageGenerationService.getPromptOptions();
      if (response.success && response.data) {
        setPromptOptions(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch prompt options:', err);
    } finally {
      setOptionsLoading(false);
    }
  };

  // Filter products by search query
  const filteredProducts = products.filter((product) =>
    product.name.toLowerCase().includes(productSearchQuery.toLowerCase()) ||
    product.description?.toLowerCase().includes(productSearchQuery.toLowerCase())
  );

  // Toggle image selection
  const toggleImageSelection = useCallback((image: ProductImage) => {
    userPickedImagesRef.current = true;
    setSelectedImages(prev => {
      const isSelected = prev.some(img => img.id === image.id);
      if (isSelected) {
        // Don't allow deselecting if it's the only selected image
        if (prev.length === 1) return prev;
        return prev.filter(img => img.id !== image.id);
      } else {
        return [...prev, image];
      }
    });
  }, []);

  // Get image category label
  const getImageCategoryLabel = (category: string): string => {
    switch (category) {
      case 'brand_logo': return 'ロゴ';
      case 'product_image': return '商品';
      case 'material': return '素材';
      default: return category;
    }
  };

  // Handle confirm
  const handleConfirm = useCallback(() => {
    if (!selectedProduct) return;

    const data: ProductSelectionData = {
      product: selectedProduct,
      selectedImages,
      segmentId: selectedIntent?.segmentId || '',
      segmentName: targetSegmentName,
      appealId: selectedIntent?.appealId || '',
      appealName: selectedIntent?.appealName || '',
      appealDirection: appealDirection,
      // 詳細指示は商品選択後の生成フォームに一本化する。
      additionalContext: '',
      includePerson,
      textlessMode,
    };

    onConfirm(data);
    onClose();
  }, [
    selectedProduct,
    selectedImages,
    selectedIntent,
    targetSegmentName,
    appealDirection,
    includePerson,
    textlessMode,
    onConfirm,
    onClose,
  ]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-6 max-[768px]:p-3"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-[1120px] flex-col overflow-hidden rounded-2xl bg-white shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] max-[768px]:max-h-[95vh] max-[768px]:max-w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="modal-header flex shrink-0 items-start justify-between rounded-t-lg border-b border-b-gray-200 bg-[#fafafa] px-6 py-5 max-[768px]:p-4">
          <h2 className="flex items-center gap-2 text-[16px] font-semibold text-gray-800 max-[768px]:text-[15px]">
            <Package size={20} className="text-violet-500" />
            商品を選択
          </h2>
          <button
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-gray-500 transition-all duration-200 ease-[ease] hover:bg-gray-100 hover:text-gray-800"
            onClick={onClose}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="modal-body flex min-h-0 flex-1 overflow-hidden max-[768px]:flex-col">
          {/* Sidebar - Product List */}
          {/* 幅・gap・ボタン寸法は削除した PromptGeneratorModal.scss がグローバルに
              勝っていた実効値。SCSS 削除後も見た目を保つため明示する。 */}
          <div className="flex w-[280px] shrink-0 flex-col border-r border-r-gray-200 bg-[#fafbfc] max-[768px]:max-h-[40vh] max-[768px]:border-b max-[768px]:border-b-gray-200 max-[768px]:border-r-0">
            <div className="relative border-b border-b-gray-200 p-3">
              <Search size={16} className="pointer-events-none absolute left-[22px] top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                className="w-full rounded-md border border-gray-300 py-2 pl-[34px] pr-2.5 text-[13px] text-gray-700 transition-[border-color,box-shadow] duration-200 ease-[ease] placeholder:text-gray-400 focus:border-violet-500 focus:shadow-[0_0_0_2px_rgba(139,92,246,0.1)] focus:outline-none"
                placeholder="商品名で検索..."
                value={productSearchQuery}
                onChange={(e) => setProductSearchQuery(e.target.value)}
              />
            </div>
            <div className="sidebar-content flex-1 overflow-y-auto p-1.5">
              {productLoading ? (
                <div className="flex flex-col items-center justify-center gap-2.5 px-3.5 py-7 text-center text-[13px] text-gray-500">
                  <Loader2 size={24} className="animate-spin" />
                  <span>読み込み中...</span>
                </div>
              ) : productError ? (
                <div className="m-1.5 flex flex-col items-center justify-center gap-2.5 rounded-md bg-red-50 p-3.5 text-center text-[13px] text-red-600">
                  <AlertCircle size={16} />
                  <span>{productError}</span>
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2.5 px-3.5 py-7 text-center text-[13px] text-gray-500">
                  <Package size={24} />
                  <span>{productSearchQuery ? '該当する商品がありません' : '商品がありません'}</span>
                </div>
              ) : (
                <div className="flex flex-col gap-1">
                  {filteredProducts.map((product) => (
                    <div
                      key={product.id}
                      className={
                        selectedProduct?.id === product.id
                          ? 'selected flex cursor-pointer items-center gap-2.5 rounded-md border border-violet-300 bg-violet-100 px-2.5 py-2 transition-all duration-150 ease-[ease]'
                          : 'flex cursor-pointer items-center gap-2.5 rounded-md border border-transparent px-2.5 py-2 transition-all duration-150 ease-[ease] hover:bg-gray-100'
                      }
                      onClick={() => setSelectedProduct(product)}
                    >
                      {product.product_images && product.product_images.length > 0 ? (
                        <img
                          src={product.product_images[0].url}
                          alt={product.name}
                          className="product-image h-10 w-10 shrink-0 rounded bg-[#f5f5f5] object-contain"
                        />
                      ) : (
                        <div className="product-image placeholder flex h-10 w-10 shrink-0 items-center justify-center rounded bg-gray-200 object-contain text-gray-400">
                          <Package size={16} />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="product-name overflow-hidden text-ellipsis whitespace-nowrap text-[13px] font-medium text-gray-800">{product.name}</div>
                      </div>
                      {selectedProduct?.id === product.id && (
                        <Check size={16} className="check-icon shrink-0 text-violet-500" />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="modal-content flex min-h-0 w-full max-w-full flex-1 flex-col overflow-y-auto overflow-x-hidden p-6 [scrollbar-color:#c1c1c1_#f1f1f1] [scrollbar-width:thin] [&::-webkit-scrollbar-thumb:hover]:bg-[#a8a8a8] [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:bg-[#c1c1c1] [&::-webkit-scrollbar-track]:rounded [&::-webkit-scrollbar-track]:bg-[#f1f1f1] [&::-webkit-scrollbar]:w-2 max-[768px]:p-4">
            {selectedProduct ? (
              <div className="flex flex-col gap-3.5">
                {/* Selected Product Info with Price */}
                <div className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 p-3 max-[768px]:flex-col max-[768px]:items-start">
                  {selectedProduct.product_images && selectedProduct.product_images.length > 0 && (
                    <img
                      src={selectedProduct.product_images[0].url}
                      alt={selectedProduct.name}
                      className="h-12 w-12 shrink-0 rounded-md bg-[#f5f5f5] object-contain max-[768px]:h-[100px] max-[768px]:w-full"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="mb-0.5 text-[14px] font-semibold text-green-800">{selectedProduct.name}</div>
                    {selectedProduct.description && (
                      <div className="text-[12px] leading-[1.4] text-green-500">
                        {selectedProduct.description.length > 80
                          ? selectedProduct.description.substring(0, 80) + '...'
                          : selectedProduct.description}
                      </div>
                    )}
                    {priceDisplay && (priceDisplay.price || priceDisplay.offer) && (
                      <div className="mt-1.5 flex items-center gap-1.5 text-[12px] text-green-800">
                        <JapaneseYen size={12} className="shrink-0" />
                        {priceDisplay.price && <span className="font-semibold">{priceDisplay.price}</span>}
                        {priceDisplay.offer && <span className="rounded bg-[rgba(22,163,74,0.1)] px-1.5 py-0.5 text-[11px] text-green-700">{priceDisplay.offer}</span>}
                      </div>
                    )}
                  </div>
                </div>

                {/* Image Selection Gallery */}
                {isBanner && availableImages.length > 0 && (
                  <div className="form-group flex flex-col gap-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <label className="flex items-center gap-1.5 text-[13px] font-medium text-gray-700">
                        <Image size={14} className="text-gray-500" />
                        画像を選択
                        <span className="ml-1 text-[11px] font-normal text-violet-500">({selectedImages.length}枚選択中)</span>
                      </label>
                      <button
                        type="button"
                        className="inline-flex shrink-0 cursor-pointer items-center gap-1 rounded-md border border-violet-300 bg-white px-2.5 py-1 text-[12px] font-medium text-violet-600 transition-all duration-150 ease-[ease] disabled:cursor-not-allowed disabled:opacity-50 enabled:hover:border-violet-400 enabled:hover:bg-violet-100"
                        onClick={() => {
                          userPickedImagesRef.current = true;
                          setSelectedImages(logoImages);
                        }}
                        disabled={logoImages.length === 0}
                      >
                        ロゴのみ選択
                      </button>
                    </div>
                    <div className="grid grid-cols-[repeat(auto-fill,minmax(80px,1fr))] gap-2">
                      {availableImages.map((image) => {
                        const isSelected = selectedImages.some(img => img.id === image.id);
                        return (
                          <div
                            key={image.id}
                            className={
                              isSelected
                                ? 'selected relative aspect-square cursor-pointer overflow-hidden rounded-lg border-2 border-violet-500 shadow-[0_0_0_2px_rgba(139,92,246,0.2)] transition-all duration-200 ease-[ease] hover:scale-[1.02]'
                                : 'relative aspect-square cursor-pointer overflow-hidden rounded-lg border-2 border-transparent transition-all duration-200 ease-[ease] hover:scale-[1.02] hover:border-violet-300'
                            }
                            onClick={() => toggleImageSelection(image)}
                          >
                            <img
                              src={image.url}
                              alt={image.title || image.file_name}
                              className="h-full w-full bg-[#f5f5f5] object-contain"
                            />
                            <div className="absolute bottom-1 left-1 rounded-[3px] bg-black/60 px-1.5 py-0.5 text-[9px] font-medium text-white">
                              {getImageCategoryLabel(image.category)}
                            </div>
                            {isSelected && (
                              <div className="absolute right-1 top-1 flex h-[22px] w-[22px] items-center justify-center rounded-[50%] bg-violet-500 text-white shadow-[0_2px_4px_rgba(0,0,0,0.2)]">
                                <CheckCircle2 size={18} />
                              </div>
                            )}
                            {(assetDefaults ? image.id === assetDefaults.primary_image_id : image.is_primary) && (
                              <div aria-label={`メイン: ${image.title || image.file_name}`} className="absolute left-1 top-1 rounded-[3px] bg-amber-500 px-1.5 py-0.5 text-[9px] font-semibold text-white">メイン</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Segment / Appeal Selection（/knowledge の戦略マップと同じマス目から1クリックで選ぶ）。
                    必要な人だけが開けるよう、既定は閉じたアコーディオンにする。 */}
                <div className="form-group overflow-hidden rounded-lg border border-gray-200">
                  <button
                    type="button"
                    aria-expanded={isIntentOpen}
                    className="flex w-full items-center justify-between gap-2 bg-gray-50 px-4 py-3 text-left transition-colors hover:bg-gray-100"
                    onClick={() => setIsIntentOpen((open) => !open)}
                  >
                    <span className="flex min-w-0 items-center gap-1.5 text-[13px] font-medium text-gray-700">
                      <Users size={14} className="shrink-0 text-gray-500" />
                      <span className="shrink-0 whitespace-nowrap">誰に、何を伝えるか</span>
                      <span className="optional ml-1 shrink-0 whitespace-nowrap text-[11px] font-normal text-gray-400">任意</span>
                      {/* 閉じていても選んだ内容を見失わないよう、見出しに要約を出す。 */}
                      {selectedIntent && (
                        <span className="ml-1 min-w-0 truncate rounded bg-violet-100 px-1.5 py-0.5 text-xs font-medium text-violet-700">
                          {selectedIntent.segmentName} / {selectedIntent.appealName}
                        </span>
                      )}
                    </span>
                    {isIntentOpen ? (
                      <ChevronUp size={18} className="shrink-0 text-gray-500" />
                    ) : (
                      <ChevronDown size={18} className="shrink-0 text-gray-500" />
                    )}
                  </button>
                  {isIntentOpen && (
                    <div className="flex flex-col gap-1.5 p-3">
                      <SegmentAppealPicker
                        product={selectedProduct}
                        selectedAppealId={selectedIntent?.appealId ?? null}
                        onSelect={setSelectedIntent}
                        onClear={() => setSelectedIntent(null)}
                      />
                      {selectedIntent?.appealDirection && (
                        <div className="rounded bg-gray-50 px-2.5 py-1.5 text-[12px] leading-[1.4] text-gray-500">
                          {selectedIntent.appealDirection}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Person Selection */}
                {isBanner && (
                <div className="form-group flex flex-col gap-1.5">
                  <label className="flex items-center gap-1.5 text-[13px] font-medium text-gray-700">
                    <UserCheck size={14} className="text-gray-500" />
                    人物表示
                    <span className="optional ml-1 text-[11px] font-normal text-gray-400">任意</span>
                  </label>
                  {optionsLoading ? (
                    <div className="flex items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-[12px] text-gray-500">
                      <Loader2 size={14} className="animate-spin" />
                    </div>
                  ) : (
                    <TaskySelect
                      value={includePerson}
                      onChange={(e) => setIncludePerson(e.target.value)}
                    >
                      <option value="">指定なし</option>
                      {promptOptions?.person_display_options &&
                        Object.entries(promptOptions.person_display_options).map(([key, label]) => (
                          <option key={key} value={key}>
                            {label}
                          </option>
                        ))}
                    </TaskySelect>
                  )}
                </div>

                )}

                {/* Textless Mode Option */}
                {isBanner && (
                <div className="form-group flex flex-col gap-1.5">
                  <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 transition-all duration-200 ease-[ease] hover:border-slate-300 hover:bg-slate-100">
                    <input
                      type="checkbox"
                      checked={textlessMode}
                      onChange={(e) => setTextlessMode(e.target.checked)}
                      className="h-4 w-4 shrink-0 cursor-pointer rounded border-2 border-slate-400 accent-violet-500 checked:border-violet-500"
                    />
                    <Type size={14} className="shrink-0 text-slate-500" />
                    <span className="text-[13px] font-medium text-gray-700">テキストなしで生成</span>
                    <span className="ml-auto text-[11px] text-gray-400">（画像内に文字を含めない）</span>
                  </label>
                </div>
                )}
              </div>
            ) : (
              <div className="empty-state flex flex-1 flex-col items-center justify-center gap-3 px-6 py-16 text-[14px] text-gray-400">
                <Package size={48} className="mb-4 opacity-50" />
                <p>左のリストから商品を選択してください</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer flex shrink-0 items-center justify-between gap-4 border-t border-t-gray-200 bg-gray-50 px-6 py-4 max-[768px]:flex-col max-[768px]:p-4 max-[640px]:gap-3">
          <button
            className="cursor-pointer rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-500 transition-all duration-200 ease-[ease] hover:bg-gray-100 hover:text-gray-800 max-[768px]:w-full max-[768px]:justify-center"
            onClick={onClose}
          >
            キャンセル
          </button>
          <button
            className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border-none bg-blue-500 px-6 py-2.5 text-[14px] font-medium text-white transition-all duration-200 ease-[ease] disabled:cursor-not-allowed disabled:bg-gray-400 disabled:opacity-50 enabled:hover:-translate-y-px enabled:hover:bg-blue-600 max-[768px]:w-full max-[768px]:justify-center"
            onClick={handleConfirm}
            disabled={!selectedProduct || assetDefaultsLoading}
          >
            <Check size={18} className="shrink-0" />
            選択を確定
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductSelectionModal;
